import React, { useEffect, useRef, useState } from "react";
import { connect, useStore } from "react-redux";
import {
    Box,
    Button,
    IconButton,
    Typography
} from "@material-ui/core";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import { KeyboardBackspace } from "@material-ui/icons";
import { ApplicationState } from "../../redux/reducers";
import { avanzarSeccionGuiaWizard, IGuiaWizard, navegarSeccionGuiaWizard } from "../../entities/guias/IGuiaWizard";
import { IGuiaTarifa } from "../../entities/guias/IGuiaTarifa";
import { setGuiaWizard } from "../../redux/actions/guiaActions";
import useRecalcularTarifaGuia from "../../hooks/useRecalcularTarifaGuia";
import useCreateGuia from "../../hooks/useCreateGuia";
import LoadingDialog from "../controls/LoadingDialog";
import { traducirButton, traducirLabel, traducirMessage } from "../../blocks/traductor";
import { useSnackbar } from "notistack";
import { generateOperationGuid } from "../../blocks/guid";
import RevisionGuiaResumenView from "./RevisionGuiaResumenView";
import {
    esCartaPorteDetalleActivo,
    esPaisEuRecoleccionGuia,
    obtenerClasificacionDesdeAreaServicio,
    validarComplementariosGuia
} from "../datosMercancia/guiaMercanciaComplementariosHelpers";

const useStyles = makeStyles(() =>
    createStyles({
        root: { padding: "16px 24px 24px" },
        actions: {
            marginTop: "24px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingLeft: 24,
            paddingRight: 24,
            paddingBottom: 24
        }
    })
);

interface StateProps {
    guiaTarifa: IGuiaTarifa;
    guiaWizard: IGuiaWizard;
}

interface DispatchProps {
    setGuiaWizard: typeof setGuiaWizard;
}

type Props = StateProps & DispatchProps;

const RevisionGuiaStep = ({
    guiaTarifa,
    guiaWizard,
    setGuiaWizard
}: Props) => {
    const classes = useStyles();
    const store = useStore<ApplicationState>();
    const { recalcular } = useRecalcularTarifaGuia();
    const { crearGuia } = useCreateGuia();
    const { enqueueSnackbar } = useSnackbar();
    const recalcAttempted = useRef(false);
    const [recalculando, setRecalculando] = useState(false);
    const [generando, setGenerando] = useState(false);

    useEffect(() => {
        if (guiaWizard.createResult) {
            setGuiaWizard(navegarSeccionGuiaWizard(guiaWizard, 5));
        }
    }, [guiaWizard.createResult]);

    useEffect(() => {
        if (guiaWizard.createResult) {
            return;
        }
        if (recalcAttempted.current) {
            return;
        }
        if (!guiaTarifa.dirty && guiaTarifa.status === "ok" && guiaTarifa.totales) {
            return;
        }
        recalcAttempted.current = true;
        setRecalculando(true);
        recalcular()
            .then(result => {
                if (!result.ok) {
                    enqueueSnackbar(result.errorMessage || traducirMessage(11), { variant: "error" });
                }
            })
            .finally(() => setRecalculando(false));
    }, []);

    const totales = guiaTarifa.totales;
    const tarifaVigente = guiaTarifa.status === "ok" && !guiaTarifa.dirty && !!totales;

    const handleRegresar = () => {
        const wizardActual = store.getState().guiaWizard;
        setGuiaWizard(navegarSeccionGuiaWizard(wizardActual, 3));
    };

    const handleGenerar = async () => {
        if (guiaWizard.createResult) {
            return;
        }
        if (!tarifaVigente || generando) {
            if (!tarifaVigente) {
                enqueueSnackbar(traducirMessage(11), { variant: "error" });
            }
            return;
        }

        const state = store.getState();
        const cartaPorteDetalleActivo = esCartaPorteDetalleActivo(state.cliente);
        const clasificacionOrigenId = obtenerClasificacionDesdeAreaServicio(state.guiaOrigen.areaServicio);
        const clasificacionDestinoId = obtenerClasificacionDesdeAreaServicio(state.guiaDestino.areaServicio);
        const resultadoComplementarios = validarComplementariosGuia(
            state.guiaMercanciasComplementarias ?? [],
            state.cliente,
            state.parametros ?? [],
            cartaPorteDetalleActivo,
            esPaisEuRecoleccionGuia(state.guiaOrigen),
            esPaisEuRecoleccionGuia(state.guiaDestino),
            clasificacionOrigenId,
            clasificacionDestinoId
        );

        if (!resultadoComplementarios.ok) {
            enqueueSnackbar(traducirMessage(resultadoComplementarios.messageId ?? 101), { variant: "error" });
            return;
        }

        let wizardActual = state.guiaWizard;
        let operationId = wizardActual.createOperationId;
        if (!operationId) {
            operationId = generateOperationGuid();
            wizardActual = { ...wizardActual, createOperationId: operationId };
            setGuiaWizard(wizardActual);
        }

        setGenerando(true);
        const result = await crearGuia(operationId);
        setGenerando(false);

        if (!result.ok) {
            enqueueSnackbar(result.errorMessage || traducirMessage(11), { variant: "error" });
            return;
        }

        const wizardPostCreate = store.getState().guiaWizard;
        setGuiaWizard({
            ...avanzarSeccionGuiaWizard(wizardPostCreate, 5),
            createOperationId: operationId,
            createResult: result.data
        });
    };

    if (guiaWizard.createResult) {
        return <LoadingDialog titulo={traducirLabel(240)} abierto={true} />;
    }

    if (recalculando || guiaTarifa.status === "loading" || generando) {
        return <LoadingDialog titulo={traducirLabel(240)} abierto={true} />;
    }

    if (guiaTarifa.status === "error" || !tarifaVigente) {
        return (
            <Box className={classes.root} textAlign="center" py={4}>
                <Typography variant="h6">{traducirLabel(264)}</Typography>
                <Typography color="error" style={{ marginTop: 16 }}>
                    {guiaTarifa.errorMessage || traducirMessage(11)}
                </Typography>
                <IconButton color="primary" style={{ marginTop: 24 }} onClick={handleRegresar} aria-label={traducirButton(9)}>
                    <KeyboardBackspace />
                </IconButton>
            </Box>
        );
    }

    return (
        <>
            <RevisionGuiaResumenView titulo={traducirLabel(264)} soloTotalGeneral />
            <Box className={classes.actions}>
                <IconButton color="primary" onClick={handleRegresar} aria-label={traducirButton(9)} disabled={generando}>
                    <KeyboardBackspace />
                </IconButton>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleGenerar}
                    disabled={generando || !tarifaVigente}
                >
                    {traducirButton(8)}
                </Button>
            </Box>
        </>
    );
};

const mapStateToProps = (state: ApplicationState) => ({
    guiaTarifa: state.guiaTarifa,
    guiaWizard: state.guiaWizard
});

const mapDispatchToProps = { setGuiaWizard };

export default connect(mapStateToProps, mapDispatchToProps)(RevisionGuiaStep);
